import React from "react";
import { Box, Typography, Grid, Card, Button } from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import pxToRem from "assets/theme/functions/pxToRem";

// Constants
import { Colors } from "utils/Constants";

// Icons
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PersonIcon from "@mui/icons-material/Person";
import GavelIcon from "@mui/icons-material/Gavel";
import DiamondIcon from "@mui/icons-material/Diamond";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const stats = [
  {
    id: "stat-clients",
    icon: <PeopleAltOutlinedIcon sx={{ fontSize: pxToRem(20), color: Colors.PRIMARY }} />,
    value: "10,000+",
    label: "Active Clients Assisted",
  },
  {
    id: "stat-satisfaction",
    icon: <VerifiedOutlinedIcon sx={{ fontSize: pxToRem(20), color: Colors.PRIMARY }} />,
    value: "99.4%",
    label: "Advisory Satisfaction",
  },
  {
    id: "stat-partners",
    icon: <AccountBalanceOutlinedIcon sx={{ fontSize: pxToRem(20), color: Colors.PRIMARY }} />,
    value: "25+",
    label: "Banking Partners",
  },
  {
    id: "stat-charges",
    icon: <ShieldOutlinedIcon sx={{ fontSize: pxToRem(20), color: Colors.PRIMARY }} />,
    value: "100%",
    label: "Zero Hidden Charges",
  },
];

const expertiseList = [
  {
    id: "exp-loans",
    icon: <DescriptionOutlinedIcon sx={{ fontSize: pxToRem(20), color: Colors.PRIMARY }} />,
    title: "Comprehensive loan advisory and servicing",
    desc: "End-to-end support for all your loan needs.",
  },
  {
    id: "exp-insurance",
    icon: <HealthAndSafetyOutlinedIcon sx={{ fontSize: pxToRem(20), color: Colors.PRIMARY }} />,
    title: "Life, Health & General Insurance solutions",
    desc: "Protection for you, your family and what matters most.",
  },
  {
    id: "exp-wealth",
    icon: <TrendingUpOutlinedIcon sx={{ fontSize: pxToRem(20), color: Colors.PRIMARY }} />,
    title: "Strategic investment and wealth planning",
    desc: "Solutions to help you grow and secure your future.",
  },
  {
    id: "exp-support",
    icon: <SupportAgentOutlinedIcon sx={{ fontSize: pxToRem(20), color: Colors.PRIMARY }} />,
    title: "Dedicated relationship-based customer support",
    desc: "Always here to assist you at every step.",
  },
];

const advantageList = [
  {
    id: "adv-experience",
    icon: <EmojiEventsIcon sx={{ fontSize: pxToRem(18), color: Colors.WHITE }} />,
    title: "Proven Experience",
    desc: "A track record of trust, reliability and consistent results.",
  },
  {
    id: "adv-client",
    icon: <PersonIcon sx={{ fontSize: pxToRem(18), color: Colors.WHITE }} />,
    title: "Client First Approach",
    desc: "Personalized solutions designed around your unique goals.",
  },
  {
    id: "adv-ethical",
    icon: <GavelIcon sx={{ fontSize: pxToRem(18), color: Colors.WHITE }} />,
    title: "Ethical & Transparent",
    desc: "Honest advice, clear processes and complete transparency.",
  },
  {
    id: "adv-e2e",
    icon: <DiamondIcon sx={{ fontSize: pxToRem(18), color: Colors.WHITE }} />,
    title: "End-to-End Solutions",
    desc: "One trusted partner for all your financial needs.",
  },
];

function AboutUs() {
  const handleConnect = () => {
    window.location.href = "mailto:connect@finvibes.net";
  };

  return (
    <DashboardLayout>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3.5, pb: 4, pt: 1 }}>
        {/* Top Hero Card */}
        <Card
          sx={{
            p: { xs: 3, sm: 4.5, md: 5 },
            borderRadius: "16px",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
            backgroundColor: Colors.WHITE,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative Dotted Grid in Top-Right */}
          <Box
            sx={{
              position: "absolute",
              top: 24,
              right: 28,
              width: 72,
              height: 48,
              backgroundImage: "radial-gradient(#93C5FD 1.5px, transparent 1.5px)",
              backgroundSize: "12px 12px",
              opacity: 0.8,
              pointerEvents: "none",
              display: { xs: "none", sm: "block" },
            }}
          />

          {/* Pill Badge */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              backgroundColor: "#EFF6FF",
              borderRadius: "20px",
              px: 1.75,
              py: 0.5,
              mb: 2,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: Colors.PRIMARY,
              }}
            />
            <Typography
              sx={{
                fontSize: pxToRem(11.5),
                color: Colors.PRIMARY,
                fontWeight: 700,
                letterSpacing: "0.04em",
              }}
            >
              ABOUT FINVIBES
            </Typography>
          </Box>

          {/* Headline */}
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: pxToRem(24), sm: pxToRem(32) },
              fontWeight: 800,
              color: "#0F172A",
              letterSpacing: "-0.5px",
              mb: 2.5,
            }}
          >
            Your Trusted{" "}
            <Typography
              component="span"
              sx={{
                fontSize: "inherit",
                fontWeight: "inherit",
                color: Colors.PRIMARY,
              }}
            >
              Financial Partner
            </Typography>
          </Typography>

          {/* Narrative Paragraphs */}
          <Typography
            sx={{
              fontSize: pxToRem(14),
              color: "#475467",
              lineHeight: 1.75,
              mb: 1.75,
              maxWidth: "920px",
            }}
          >
            Finvibes is a professional financial services platform built on trust, expertise, and a
            proven record of success. Over the years, we have successfully guided individuals and
            businesses toward smarter financial decisions through tailored solutions and seamless
            execution.
          </Typography>
          <Typography
            sx={{
              fontSize: pxToRem(14),
              color: "#475467",
              lineHeight: 1.75,
              mb: 3.5,
              maxWidth: "920px",
            }}
          >
            Our services span loans, insurance, and investments, delivered with precision,
            transparency, and long-term perspective. At Finvibes, every client engagement is handled
            with care, confidentiality, and a commitment to excellence.
          </Typography>

          {/* 4 Stats Grid */}
          <Box
            sx={{
              backgroundColor: "#F8FAFC",
              border: "1px solid #E2E8F0",
              borderRadius: "14px",
              p: { xs: 2, sm: 2.5 },
            }}
          >
            <Grid container spacing={2}>
              {stats.map((stat) => (
                <Grid item xs={6} md={3} key={stat.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        backgroundColor: "#EFF6FF",
                        border: "1px solid #DBEAFE",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {stat.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: pxToRem(17),
                          fontWeight: 800,
                          color: "#0F172A",
                          lineHeight: 1.2,
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: pxToRem(11.5),
                          color: "#64748B",
                          lineHeight: 1.3,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Card>

        {/* Two Column Section: Expertise vs Advantage */}
        <Grid container spacing={3.5}>
          {/* Left Column: Our Expertise */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: pxToRem(20),
                  fontWeight: 800,
                  color: "#0F172A",
                  mb: 0.5,
                }}
              >
                Our Expertise
              </Typography>
              <Box
                sx={{
                  width: 36,
                  height: 3,
                  backgroundColor: Colors.PRIMARY,
                  borderRadius: "2px",
                }}
              />
            </Box>

            <Card
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: "16px",
                border: "1px solid #E2E8F0",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
                backgroundColor: Colors.WHITE,
                display: "flex",
                flexDirection: "column",
                gap: 2.25,
                height: "calc(100% - 36px)",
              }}
            >
              {expertiseList.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.75,
                    p: 1.5,
                    borderRadius: "12px",
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #F1F5F9",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#F0F6FE",
                      borderColor: "#DBEAFE",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      backgroundColor: "#EFF6FF",
                      border: "1px solid #DBEAFE",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: pxToRem(14),
                        fontWeight: 700,
                        color: "#101828",
                        lineHeight: 1.35,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: pxToRem(12.5),
                        color: "#64748B",
                        lineHeight: 1.4,
                        mt: 0.35,
                      }}
                    >
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Card>
          </Grid>

          {/* Right Column: The Finvibes Advantage */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: pxToRem(20),
                  fontWeight: 800,
                  color: "#0F172A",
                  mb: 0.5,
                }}
              >
                The Finvibes Advantage
              </Typography>
              <Box
                sx={{
                  width: 36,
                  height: 3,
                  backgroundColor: Colors.PRIMARY,
                  borderRadius: "2px",
                }}
              />
            </Box>

            <Card
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: "16px",
                border: "1px solid #E2E8F0",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
                backgroundColor: Colors.WHITE,
                display: "flex",
                flexDirection: "column",
                gap: 2.25,
                height: "calc(100% - 36px)",
              }}
            >
              {advantageList.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.75,
                    p: 1.5,
                    borderRadius: "12px",
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #F1F5F9",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#F0F6FE",
                      borderColor: "#DBEAFE",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: Colors.PRIMARY,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: pxToRem(14),
                        fontWeight: 700,
                        color: "#101828",
                        lineHeight: 1.35,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: pxToRem(12.5),
                        color: "#64748B",
                        lineHeight: 1.4,
                        mt: 0.35,
                      }}
                    >
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Card>
          </Grid>
        </Grid>

        {/* Bottom CTA Banner */}
        <Card
          sx={{
            p: { xs: 2.5, sm: 3.5 },
            borderRadius: "16px",
            backgroundColor: "#F0F7FF",
            border: "1px solid #DBEAFE",
            boxShadow: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1, minWidth: "260px" }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: Colors.PRIMARY,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: Colors.WHITE,
                flexShrink: 0,
              }}
            >
              <FormatQuoteIcon sx={{ fontSize: pxToRem(26) }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: pxToRem(14), sm: pxToRem(15) },
                  fontWeight: 700,
                  color: "#0F172A",
                  lineHeight: 1.4,
                }}
              >
                At Finvibes, we go beyond transactions — we create enduring financial value and
                confidence.
              </Typography>
              <Typography
                sx={{
                  fontSize: pxToRem(12.5),
                  color: "#64748B",
                  mt: 0.25,
                }}
              >
                Your journey to financial independence and stability starts here.
              </Typography>
            </Box>
          </Box>

          <Button
            onClick={handleConnect}
            endIcon={<ArrowForwardIcon sx={{ fontSize: pxToRem(16) }} />}
            sx={{
              backgroundColor: Colors.PRIMARY,
              color: Colors.WHITE,
              textTransform: "none",
              fontSize: pxToRem(13.5),
              fontWeight: 700,
              borderRadius: "8px",
              px: 3,
              py: 1.2,
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: "#032d7b",
                color: Colors.WHITE,
              },
            }}
          >
            Let&apos;s connect!
          </Button>
        </Card>
      </Box>
    </DashboardLayout>
  );
}

export default AboutUs;
